import { useEffect, useState } from 'react'
import ConfirmDialog from '../ConfirmDialog'
import { clipFactoryAPI, clipFactoryErrorText, type ClipHistory } from './clipFactoryApi'

/** What an edge shift does, in words: "start 1.0 s later, end 1.5 s earlier". */
export const edgesSentence = (edges: { start: number; end: number }) => {
  const part = (name: string, s: number, later: string, earlier: string) =>
    s === 0 ? null : `${name} ${Math.abs(s).toFixed(1)} s ${s > 0 ? later : earlier}`
  return [part('start', edges.start, 'later', 'earlier'), part('end', edges.end, 'later', 'earlier')]
    .filter(Boolean)
    .join(', ')
}

/**
 * Your clip history: the clips you export are kept (titles and edges, no
 * video) after the video expires, and once there are enough of them new
 * suggestions' edges move to how you usually cut. Off stops both; Clear
 * forgets it. Hidden when Clip Factory has no account for you.
 */
const ClipHistoryPanel = () => {
  const [state, setState] = useState<ClipHistory | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [clearing, setClearing] = useState(false)

  useEffect(() => {
    let live = true
    clipFactoryAPI
      .history()
      .then((h) => live && setState(h))
      .catch(() => live && setState(null))
    return () => {
      live = false
    }
  }, [])

  if (!state) return null

  const run = async (action: () => Promise<ClipHistory>, fallback: string) => {
    setBusy(true)
    setError(null)
    try {
      setState(await action())
    } catch (e) {
      setError(clipFactoryErrorText(e, fallback))
    } finally {
      setBusy(false)
    }
  }

  const needed = Math.max(0, state.min_exports - state.exports)

  return (
    <section className="bg-white rounded-lg shadow p-5" aria-label="Your clip history" data-testid="clip-history">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h3 className="text-lg font-semibold text-gray-900">Your clip history</h3>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => run(() => clipFactoryAPI.setHistory(!state.enabled), "Couldn't change the setting.")}
            disabled={busy}
            className="rounded-md border border-gray-300 px-3 py-1 text-sm text-gray-700 hover:bg-gray-100 disabled:opacity-50"
          >
            {state.enabled ? 'Turn off' : 'Turn on'}
          </button>
          {state.exports > 0 && (
            <button
              type="button"
              onClick={() => setClearing(true)}
              disabled={busy}
              className="rounded-md border border-gray-300 px-3 py-1 text-sm text-danger-600 hover:bg-gray-100 disabled:opacity-50"
            >
              Clear
            </button>
          )}
        </div>
      </div>
      <p className="mt-1 text-sm text-gray-600" data-testid="clip-history-status">
        {!state.enabled
          ? 'Off: exports aren’t kept, and suggestions keep the edges the picker chose.'
          : state.edges
            ? `New suggestions are cut the way you usually cut clips: ${edgesSentence(state.edges)} than picked, from ${state.edges.exports} exports across ${state.edges.videos} videos.`
            : state.exports >= state.min_exports && state.videos >= state.min_videos
              ? `${state.exports} exports across ${state.videos} videos: you keep the picker's edges, so suggestions stay as picked.`
              : `${state.exports} ${state.exports === 1 ? 'export' : 'exports'} kept so far. Suggestions start following how you cut clips after ${state.min_exports} exports from at least ${state.min_videos} videos${needed ? ` (${needed} more)` : ''}.`}
      </p>
      <p className="mt-1 text-xs text-gray-500">
        Kept for each clip you export from a suggestion: its title, opening words and edges, not the video. Deleting a
        video removes its clips from the history.
      </p>
      {error && (
        <p role="alert" className="mt-2 text-sm text-danger-600">
          {error}
        </p>
      )}
      <ConfirmDialog
        isOpen={clearing}
        title="Clear your clip history?"
        message="Suggestions go back to the picker's edges until you've exported enough clips again."
        confirmLabel="Clear"
        danger
        confirming={busy}
        onConfirm={() => run(() => clipFactoryAPI.clearHistory(), "Couldn't clear the history.").then(() => setClearing(false))}
        onCancel={() => setClearing(false)}
      />
    </section>
  )
}

export default ClipHistoryPanel
